import type { ButtonNode, MenuNode, MenuSchema, TextInputNode, TextNode } from '../MenuSchema.js';

export const USER_ADMIN_MENU_ACTIONS = {
    createUser: 'user-admin-create-user',
    updateSelected: 'user-admin-update-selected',
    refreshList: 'user-admin-refresh-list',
    setPassword: 'user-admin-set-password',
    depositTokens: 'user-admin-deposit-tokens',
} as const;

export const USER_ADMIN_MENU_LABELS = {
    title: 'User Admin',
    loadingUsers: 'Loading users…',
    username: 'Username',
    usernameWithColon: 'Username:',
    usernameRequired: 'Username is required.',
    passwordNewUser: 'Password (new user):',
    passwordNewUserLong: 'Password (new user only)',
    passwordMinPlaceholder: 'minimum 8 characters',
    displayName: 'Display Name',
    displayNamePlaceholder: 'display name',
    isEntertainer: 'Entertainer Privilege',
    tokenBalance: 'Token Balance',
    tokenBalanceUnknown: 'Token Balance: -',
    depositAmount: 'Deposit Amount',
    depositAmountPlaceholder: 'token amount',
    depositTokens: 'Deposit Tokens',
    usernamePlaceholder: 'username',
    createUser: 'Create User',
    clearForm: 'Clear Form',
    refreshList: 'Refresh List',
    updateSelected: 'Update Selected',
    setPassword: 'Set Password',
    notAuthenticated: 'Not authenticated.',
    adminAccessRequired: 'Admin access required.',
    failedToLoad: 'Failed to load.',
    failedToCreateUser: 'Failed to create user.',
    networkError: 'Network error.',
} as const;

export function createUserAdminMenuSchema(): MenuSchema {
    return {
        id: 'USERADMIN',
        title: USER_ADMIN_MENU_LABELS.title,
        nodes: [
            { type: 'text', text: USER_ADMIN_MENU_LABELS.loadingUsers },
            { type: 'text', text: USER_ADMIN_MENU_LABELS.username },
            { type: 'input', id: 'user-admin-username-input', placeholder: USER_ADMIN_MENU_LABELS.usernamePlaceholder, get: () => '' },
            { type: 'text', text: USER_ADMIN_MENU_LABELS.displayName },
            { type: 'input', id: 'user-admin-display-name-input', placeholder: USER_ADMIN_MENU_LABELS.displayNamePlaceholder, get: () => '' },
            { type: 'text', text: USER_ADMIN_MENU_LABELS.passwordNewUser },
            { type: 'input', id: 'user-admin-password-input', placeholder: USER_ADMIN_MENU_LABELS.passwordMinPlaceholder, get: () => '' },
            { type: 'input', id: 'user-admin-deposit-amount-input', placeholder: USER_ADMIN_MENU_LABELS.depositAmountPlaceholder, get: () => '' },
            { type: 'button', id: USER_ADMIN_MENU_ACTIONS.createUser, label: USER_ADMIN_MENU_LABELS.createUser },
            { type: 'button', id: USER_ADMIN_MENU_ACTIONS.updateSelected, label: USER_ADMIN_MENU_LABELS.updateSelected },
            { type: 'button', id: USER_ADMIN_MENU_ACTIONS.setPassword, label: USER_ADMIN_MENU_LABELS.setPassword },
            { type: 'button', id: USER_ADMIN_MENU_ACTIONS.depositTokens, label: USER_ADMIN_MENU_LABELS.depositTokens },
            { type: 'button', id: USER_ADMIN_MENU_ACTIONS.refreshList, label: USER_ADMIN_MENU_LABELS.refreshList },
        ],
    };
}

function findSchemaNodeById(schema: MenuSchema, id: string): MenuNode | undefined {
    return schema.nodes.find((n) => (n as { id?: string }).id === id);
}

export function readUserAdminSchemaButtonLabel(schema: MenuSchema, id: string, fallback: string): string {
    const node = findSchemaNodeById(schema, id) as ButtonNode | undefined;
    if (!node || node.type !== 'button') {
        return fallback;
    }
    return typeof node.label === 'function' ? node.label() : node.label;
}

export function readUserAdminSchemaInputPlaceholder(schema: MenuSchema, id: string, fallback: string): string {
    const node = findSchemaNodeById(schema, id) as TextInputNode | undefined;
    if (!node || node.type !== 'input') {
        return fallback;
    }
    return node.placeholder || fallback;
}

export function readUserAdminSchemaTextAt(schema: MenuSchema, index: number, fallback: string): string {
    const textNodes = schema.nodes.filter((n): n is TextNode => n.type === 'text');
    const node = textNodes[index];
    if (!node) {
        return fallback;
    }
    return typeof node.text === 'function' ? node.text() : node.text;
}
